import React from 'react';
import { ChartBar, CheckCircle, XCircle } from '@phosphor-icons/react';

const REC_COLORS = {
  STRONG_BUY: '#10B981',
  BUY: '#22C55E',
  NEUTRAL: '#94A3B8',
  SELL: '#F97316',
  STRONG_SELL: '#EF4444',
};

const TV_INTERVALS = ['15m', '1h', '4h', '1D', '1W'];

export function FundamentalsCard({ fundamentals }) {
  if (!fundamentals || fundamentals.error) return null;
  const ratios = Object.entries(fundamentals.ratios || {}).slice(0, 8);
  const pros = (fundamentals.pros || []).slice(0, 3);
  const cons = (fundamentals.cons || []).slice(0, 3);

  return (
    <div className="bg-[#12141A] border border-white/10 p-5" data-testid="stock-fundamentals-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-heading font-bold text-white flex items-center gap-2">
          <ChartBar size={18} weight="duotone" className="text-[#2563EB]" />
          Fundamentals (Screener.in)
        </h3>
        {fundamentals.url && (
          <a
            href={fundamentals.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] text-[#2563EB] hover:underline"
          >
            Full report ↗
          </a>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        {ratios.map(([n, v]) => (
          <div key={n} className="border border-white/5 p-2">
            <div className="text-[9px] uppercase text-[#64748B] tracking-wide">{n}</div>
            <div className="text-xs font-mono text-white mt-0.5">{v}</div>
          </div>
        ))}
      </div>

      {(pros.length > 0 || cons.length > 0) && (
        <div className="grid grid-cols-2 gap-3">
          <ProsConsColumn label="Pros" items={pros} color="text-[#10B981]" Icon={CheckCircle} />
          <ProsConsColumn label="Cons" items={cons} color="text-[#EF4444]" Icon={XCircle} />
        </div>
      )}
    </div>
  );
}

function ProsConsColumn({ label, items, color, Icon }) {
  return (
    <div>
      <p className={`text-[10px] uppercase mb-1 ${color}`}>{label}</p>
      {items.map((text) => (
        <div key={text} className="flex items-start gap-1 text-[11px] text-[#94A3B8] mb-1">
          <Icon size={12} weight="fill" className={`${color} mt-0.5 shrink-0`} />
          <span>{text}</span>
        </div>
      ))}
    </div>
  );
}

export function TvRatingCard({ tvRating }) {
  if (!tvRating || !tvRating.intervals) return null;
  return (
    <div className="bg-[#12141A] border border-white/10 p-5" data-testid="stock-tv-rating-card">
      <h3 className="text-sm font-heading font-bold text-white mb-3 flex items-center gap-2">
        <ChartBar size={18} weight="duotone" className="text-[#F59E0B]" />
        TradingView Rating
      </h3>
      <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
        {TV_INTERVALS.map((iv) => (
          <IntervalCell key={iv} interval={iv} rec={tvRating.intervals[iv]} />
        ))}
      </div>
    </div>
  );
}

function IntervalCell({ interval, rec }) {
  const rating = rec?.recommendation || 'N/A';
  const color = REC_COLORS[rating] || '#94A3B8';
  return (
    <div className="p-2 border border-white/5">
      <div className="text-[9px] text-[#64748B]">{interval}</div>
      <div className="text-[11px] font-bold mt-0.5" style={{ color }}>
        {rating.replace('_', ' ')}
      </div>
    </div>
  );
}
